import type { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import type {
  CommonSite,
  SearchCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'

import { cleanObjectOf } from 'src/utils/helpers'

export interface BuildApiUrlArgs {
  brand: Brand
  propertyId: string
  salesMarket?: string
  searchCriteria: SearchCriteria
}

const exludeFromApiUrl = [
  'destinationURL',
  'discounts',
  'regionName',
  'page',
  'sort',
  'grade',
  'rating',
  'index',
  'siteId',
  'propertyId',
  'accommodationTypes',
  'salesMarket',
  'ne',
  'sw',
  'sref',
] satisfies (keyof SearchCriteria)[]

const omit = <T extends object, U extends keyof T>(
  obj: T,
  keys: U[],
): Omit<T, U> =>
  (Object.keys(obj) as U[]).reduce(
    (acc, curr) => (keys.includes(curr) ? acc : { ...acc, [curr]: obj[curr] }),
    {} as Omit<T, U>,
  )

const convertAllValuesToString = (searchCriteria: SearchCriteria) => {
  const {
    features,
    bathrooms,
    minbathrooms,
    bedrooms,
    minbedrooms,
    minPrice,
    maxPrice,
    siteFeatures,
    nights,
    range,
    start,
    adult,
    child,
    infant,
    pets,
    accommodationType,
  } = searchCriteria

  return {
    features: features?.toString(),
    bathrooms,
    minbathrooms,
    bedrooms,
    minbedrooms,
    minPrice: minPrice?.toString(),
    maxPrice: maxPrice?.toString(),
    siteFeatures: siteFeatures?.toString(),
    nights: nights?.toString(),
    range: range?.toString(),
    start,
    adult: adult?.toString(),
    child: child?.toString(),
    infant: infant?.toString(),
    pets: pets?.toString(),
    ...(accommodationType && { accommodationType }),
  }
}

export const buildApiUrl = (args: BuildApiUrlArgs): string => {
  const { brand, propertyId, salesMarket, searchCriteria } = args
  const relevantSearchCriteria = omit(searchCriteria, exludeFromApiUrl)
  const finalSearchCriteria = convertAllValuesToString(relevantSearchCriteria)
  const flattenedObject = {
    brand,
    propertyId,
    ...(salesMarket && { salesMarket: salesMarket }),
    ...finalSearchCriteria,
  }
  const cleanedArgs = cleanObjectOf({
    data: flattenedObject,
    filterOutValues: [undefined, null, ''],
  })
  const params = new URLSearchParams(Object.entries(cleanedArgs))

  const endpoint = '/api/search/properties/list'

  return `${endpoint}?${params.toString()}`
}

type GetPropertyInfoArgs = BuildApiUrlArgs

export interface PropertyInfoResponse {
  success: boolean
  property: CommonSite | null
}
export const getPropertyInfo = async (
  args: GetPropertyInfoArgs,
  clientHeader: string,
  currency: string,
  contentfulLocale: string,
): Promise<PropertyInfoResponse> => {
  const result = await fetch(buildApiUrl(args), {
    headers: {
      'x-awaze-client': clientHeader,
      'x-awaze-currency': currency,
      'x-awaze-locale': contentfulLocale,
    },
  })
  if (result.status !== 200) {
    return {
      success: false,
      property: null,
    }
  }
  const parsed = await result.json()
  return { success: true, property: parsed.properties[0] }
}
